import { useEffect, useState, useRef } from 'react';
import { ChefHat, Clock, RefreshCw, Check, Utensils, Package } from 'lucide-react';
import type { PedidoDetalle, PedidoItemVista, PedidoVista } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourCocina } from '@renderer/tours';

const REFRESCO_MS = 10_000;

export function CocinaPage() {
  const [pedidos, setPedidos] = useState<PedidoVista[]>([]);
  const [detalles, setDetalles] = useState<Record<number, PedidoDetalle>>({});
  const [cargando, setCargando] = useState(true);
  const [marcandoListo, setMarcandoListo] = useState<number | null>(null);
  // Sprint 12.1d — entregado en mesa (comer_aqui)
  const [marcandoEntregado, setMarcandoEntregado] = useState<number | null>(null);
  /** Sprint 66 — Paso 7: la cocina avisa que ya está en la bolsa. */
  const [marcandoEmpaquetado, setMarcandoEmpaquetado] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const negocio = useNegocioStore((s) => s.negocio);

  const cargar = async () => {
    const res = await window.api.pedidos.listarPorEstado([
      'pendiente_cocina',
      'cocina',
      'listo',
    ]);
    if (res.ok) {
      setPedidos(res.data);
      // Sprint 28 — refrescar el detalle de TODOS los pedidos en pantalla.
      // Antes solo se cargaba el de los pedidos nuevos; si a un pedido ya
      // cacheado se le agregaba una tanda (agregarItems), esos renglones no
      // aparecían hasta reiniciar la app.
      const mapa: Record<number, PedidoDetalle> = {};
      await Promise.all(
        res.data.map(async (p) => {
          const d = await window.api.pedidos.obtener(p.id);
          if (d.ok && d.data) mapa[p.id] = d.data;
        }),
      );
      setDetalles(mapa);
    }
    setCargando(false);
  };

  useEffect(() => {
    cargar();
    intervalRef.current = setInterval(cargar, REFRESCO_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMarcarListo = async (id: number) => {
    setMarcandoListo(id);
    const res = await window.api.pedidos.marcarListo(id);
    setMarcandoListo(null);
    if (res.ok) {
      await cargar();
    } else {
      window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
    }
  };

  /**
   * Sprint 66 — Paso 7 del flujo de domicilio. No cambia el estado del pedido: sólo sella la
   * hora, para que la caja sepa que ya puede llamar al repartidor.
   */
  const onMarcarEmpaquetado = async (id: number) => {
    setMarcandoEmpaquetado(id);
    const res = await window.api.pedidos.marcarEmpaquetado(id);
    setMarcandoEmpaquetado(null);
    if (res.ok) {
      await cargar();
    } else {
      window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
    }
  };

  // Sprint 12.1d — Mesero llevó el plato a la mesa (comer_aqui: listo → entregado).
  const onMarcarEntregadoMesa = async (id: number) => {
    setMarcandoEntregado(id);
    const res = await window.api.pedidos.marcarEntregadoMesa({ pedidoId: id });
    setMarcandoEntregado(null);
    if (res.ok) {
      await cargar();
    } else {
      window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
    }
  };

  const pendientes = pedidos.filter((p) => p.estado === 'pendiente_cocina' || p.estado === 'cocina');
  const listos = pedidos.filter((p) => p.estado === 'listo');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between" data-tour="cocina-header">
        <div className="flex items-center gap-3">
          <ChefHat className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Cocina</h1>
            <p className="text-sm text-slate-500">
              {pendientes.length} pendiente{pendientes.length !== 1 ? 's' : ''} · {listos.length} listo{listos.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={cargar} className="btn-ghost" aria-label="Refrescar">
            <RefreshCw className="h-4 w-4" />
            Refrescar
          </button>
          <HelpButton tour={tourCocina} />
        </div>
      </div>

      {cargando ? (
        <div className="text-center text-slate-400 py-12">Cargando...</div>
      ) : pedidos.length === 0 ? (
        <div className="card text-center text-slate-400 py-12">
          <ChefHat className="mx-auto mb-3 h-12 w-12 opacity-30" />
          <p>No hay pedidos en cocina.</p>
          <p className="text-xs">Los nuevos pedidos aparecerán aquí automáticamente.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {pendientes.length > 0 && (
            <section data-tour="cocina-pendientes">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-orange-600">
                ⏱️ Pendientes ({pendientes.length})
              </h2>
              {/* Sprint 30 — sub-agrupado por destino: a domicilio se empaca,
                  comer aquí se emplata. */}
              <div className="grid gap-5 md:grid-cols-2">
                <GrupoDestino
                  tipo="delivery"
                  pedidos={pendientes.filter((p) => p.tipo === 'delivery')}
                  detalles={detalles}
                  monedaSimbolo={negocio?.monedaSimbolo ?? '$'}
                  locale={negocio?.locale ?? 'es-MX'}
                  estado="pendiente"
                  onMarcarListo={onMarcarListo}
                  marcandoListoId={marcandoListo}
                />
                <GrupoDestino
                  tipo="comer_aqui"
                  pedidos={pendientes.filter((p) => p.tipo === 'comer_aqui')}
                  detalles={detalles}
                  monedaSimbolo={negocio?.monedaSimbolo ?? '$'}
                  locale={negocio?.locale ?? 'es-MX'}
                  estado="pendiente"
                  onMarcarListo={onMarcarListo}
                  marcandoListoId={marcandoListo}
                />
              </div>
            </section>
          )}

          {listos.length > 0 && (
            <section data-tour="cocina-listos">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-green-600">
                ✅ Listos para entregar ({listos.length})
              </h2>
              <div className="grid gap-5 md:grid-cols-2">
                <GrupoDestino
                  tipo="delivery"
                  pedidos={listos.filter((p) => p.tipo === 'delivery')}
                  detalles={detalles}
                  monedaSimbolo={negocio?.monedaSimbolo ?? '$'}
                  locale={negocio?.locale ?? 'es-MX'}
                  estado="listo"
                  onMarcarListo={onMarcarListo}
                  marcandoListoId={marcandoListo}
                  onMarcarEntregadoMesa={onMarcarEntregadoMesa}
                  marcandoEntregadoId={marcandoEntregado}
                  onMarcarEmpaquetado={onMarcarEmpaquetado}
                  marcandoEmpaquetadoId={marcandoEmpaquetado}
                />
                <GrupoDestino
                  tipo="comer_aqui"
                  pedidos={listos.filter((p) => p.tipo === 'comer_aqui')}
                  detalles={detalles}
                  monedaSimbolo={negocio?.monedaSimbolo ?? '$'}
                  locale={negocio?.locale ?? 'es-MX'}
                  estado="listo"
                  onMarcarListo={onMarcarListo}
                  marcandoListoId={marcandoListo}
                  onMarcarEntregadoMesa={onMarcarEntregadoMesa}
                  marcandoEntregadoId={marcandoEntregado}
                  onMarcarEmpaquetado={onMarcarEmpaquetado}
                  marcandoEmpaquetadoId={marcandoEmpaquetado}
                />
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Sprint 30 — Sub-grupo de la cocina por destino. Encabezado de color +
 * grid de tarjetas. No renderiza nada si el grupo está vacío.
 */
function GrupoDestino({
  tipo,
  pedidos,
  detalles,
  monedaSimbolo,
  locale,
  estado,
  onMarcarListo,
  marcandoListoId,
  onMarcarEntregadoMesa,
  marcandoEntregadoId,
  onMarcarEmpaquetado,
  marcandoEmpaquetadoId,
}: {
  tipo: 'comer_aqui' | 'delivery';
  pedidos: PedidoVista[];
  detalles: Record<number, PedidoDetalle>;
  monedaSimbolo: string;
  locale: string;
  estado: 'pendiente' | 'listo';
  onMarcarListo: (id: number) => void;
  marcandoListoId: number | null;
  onMarcarEntregadoMesa?: (id: number) => void;
  marcandoEntregadoId?: number | null;
  onMarcarEmpaquetado?: (id: number) => void;
  marcandoEmpaquetadoId?: number | null;
}) {
  if (pedidos.length === 0) return null;
  const esDomicilio = tipo === 'delivery';
  return (
    <div>
      <div
        className={`mb-1.5 flex items-center gap-2 rounded-md px-2 py-1 text-xs font-bold ${
          esDomicilio ? 'bg-orange-100 text-orange-800' : 'bg-emerald-100 text-emerald-800'
        }`}
      >
        <span>
          {esDomicilio
            ? '📦 A domicilio — empacar en contenedor + bolsa'
            : '🍽️ Comer aquí — emplatar'}
        </span>
        <span className="ml-auto rounded-full bg-white/70 px-2 py-0.5">{pedidos.length}</span>
      </div>
      {/* Sprint 30 — el grupo vive en media pantalla (2 columnas por destino);
          xl:grid-cols-2 mantiene tarjetas legibles sin desperdiciar ancho. */}
      <div className="grid gap-3 lg:grid-cols-2">
        {pedidos.map((p) => (
          <PedidoCocinaCard
            key={p.id}
            pedido={p}
            detalle={detalles[p.id]}
            monedaSimbolo={monedaSimbolo}
            locale={locale}
            estado={estado}
            onMarcarListo={onMarcarListo}
            marcando={marcandoListoId === p.id}
            onMarcarEntregadoMesa={onMarcarEntregadoMesa}
            marcandoEntregado={marcandoEntregadoId === p.id}
            onMarcarEmpaquetado={onMarcarEmpaquetado}
            marcandoEmpaquetado={marcandoEmpaquetadoId === p.id}
          />
        ))}
      </div>
    </div>
  );
}

function PedidoCocinaCard({
  pedido,
  detalle,
  monedaSimbolo,
  locale,
  onMarcarListo,
  marcando,
  estado,
  onMarcarEntregadoMesa,
  marcandoEntregado,
  onMarcarEmpaquetado,
  marcandoEmpaquetado,
}: {
  pedido: PedidoVista;
  detalle?: PedidoDetalle;
  monedaSimbolo: string;
  locale: string;
  onMarcarListo: (id: number) => void;
  marcando: boolean;
  estado: 'pendiente' | 'listo';
  onMarcarEntregadoMesa?: (id: number) => void;
  marcandoEntregado?: boolean;
  onMarcarEmpaquetado?: (id: number) => void;
  marcandoEmpaquetado?: boolean;
}) {
  const minutosTranscurridos = Math.floor(
    (Date.now() - new Date(pedido.fechaCreacion).getTime()) / 60_000,
  );
  const urgente = minutosTranscurridos >= 15 && estado === 'pendiente';

  // Sprint 28 — un renglón es de "tanda agregada" si se creó bastante después
  // del pedido. Umbral de 90 s para no marcar los renglones originales, que se
  // insertan segundos después del pedido dentro de la misma transacción.
  const pedidoCreadoMs = new Date(pedido.fechaCreacion).getTime();
  const esItemNuevo = (it: PedidoItemVista): boolean =>
    !!it.fechaCreacion &&
    new Date(it.fechaCreacion).getTime() - pedidoCreadoMs > 90_000;
  const tieneTandaNueva = (detalle?.items ?? []).some(esItemNuevo);

  const esDomicilio = pedido.tipo === 'delivery';
  return (
    <div
      className={`card flex flex-col gap-2 border-l-4 ${
        esDomicilio ? 'border-l-orange-400' : 'border-l-emerald-500'
      } ${urgente ? 'bg-red-50' : ''}`}
    >
      {/* Sprint 30 — banner de destino: la cocina sabe de un vistazo cómo se arma. */}
      <div
        className={`rounded-md px-2 py-1 text-xs font-bold text-white ${
          esDomicilio ? 'bg-orange-500' : pedido.paraRecoger ? 'bg-teal-600' : 'bg-emerald-600'
        }`}
      >
        {/*
          Sprint 104 — Tres destinos, no dos.

          "Pasa a recogerlo" se empaca como un domicilio pero **el cliente está esperando en el
          local**: es la diferencia entre una bolsa que sale en moto y una que hay que entregar
          llamando a alguien por su nombre. Cocina necesita saber cuál de las dos está haciendo.
        */}
        {esDomicilio
          ? '📦 A domicilio — empacar (contenedor + bolsa)'
          : pedido.paraRecoger
            ? '🛍️ Pasa a recogerlo — empacar, el cliente espera'
            : '🍽️ Comer aquí — emplatar'}
      </div>
      <div className="flex items-start justify-between">
        {/*
          Sprint 128 — El nombre manda; el folio pasa a ser la referencia.

          Estaba al revés: el folio en 24 px negrita y el nombre —cuando salía— en 12 px gris
          debajo. La dueña lo dijo sin rodeos: *"para nosotros es más fácil por su nombre que por
          folio"*. Nadie grita "cero cero cero doce" en un mostrador.

          El folio no se quita: es con lo que se rastrea un pedido cuando algo sale mal. Sólo deja
          de ser lo único que se lee de lejos.
        */}
        <div className="min-w-0">
          {pedido.clienteNombre ? (
            <>
              <div className="truncate text-2xl font-bold leading-tight">
                {pedido.clienteNombre}
              </div>
              <div className="text-xs text-slate-600">
                #{String(pedido.numeroPedido).padStart(5, '0')}
              </div>
            </>
          ) : (
            // Sin nombre no se inventa nada: el folio vuelve a ser lo grande. El canal ya lo dice
            // el banner de arriba, así que repetirlo aquí sería decir dos veces lo mismo.
            <div className="text-2xl font-bold">#{String(pedido.numeroPedido).padStart(5, '0')}</div>
          )}
        </div>
        <div className={`flex items-center gap-1 text-xs ${urgente ? 'font-bold text-red-600' : 'text-slate-500'}`}>
          <Clock className="h-3 w-3" />
          {minutosTranscurridos}m
        </div>
      </div>

      {tieneTandaNueva && (
        <div className="rounded-md bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900">
          🆕 Esta mesa pidió más — revisa los renglones marcados
        </div>
      )}

      <div className="border-t border-slate-200 pt-2 space-y-1.5">
        {detalle?.items.map((it) => {
          const nuevo = esItemNuevo(it);
          return (
          <div
            key={it.id}
            className={`text-sm ${nuevo ? '-mx-1.5 rounded-md bg-amber-50 px-1.5 py-1' : ''}`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-semibold">{it.cantidad}× {it.productoNombre ?? it.paqueteNombre}</span>
              {nuevo && (
                <span className="shrink-0 rounded-full bg-amber-300 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-900">
                  🆕 Nuevo
                </span>
              )}
            </div>
            {/* Sprint 14.3 — Desglose del paquete con productos finales.
                Sin esto, la cocina veía sólo "Espadas Cerdo" y no sabía qué
                cortes preparar ni si había sustituciones del cliente. */}
            {it.paqueteProductos && it.paqueteProductos.length > 0 && (
              <ul className="mt-0.5 ml-3 space-y-0.5 text-xs text-slate-700">
                {it.paqueteProductos.map((pp) => (
                  <li key={pp.posicion} className="flex items-baseline gap-1">
                    <span className="text-slate-400">•</span>
                    {/*
                      Sprint 138 — El renglón que el cliente quitó se sigue viendo, y se ve como
                      quitado. Antes se pintaba "1× " con el nombre en blanco: un renglón vacío en
                      la comanda no se lee como "no lleva ensalada", se lee como que el sistema
                      falló, y alguien acaba sirviéndola por si acaso.
                    */}
                    {pp.excluido ? (
                      <span className="font-semibold text-rose-700">SIN {pp.productoBaseNombre}</span>
                    ) : (
                      <span>
                        <span className="font-medium">{pp.cantidad}× {pp.productoFinalNombre}</span>
                        {pp.esSustituido && (
                          <span className="ml-1 text-amber-600 italic">
                            (en lugar de {pp.productoBaseNombre})
                          </span>
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {it.personalizacionJson && <PersonalizacionDetalle json={it.personalizacionJson} />}
            {/*
              Sprint 138 — La nota es la instrucción que decide si el plato regresa ("término
              medio", "bien picada"). En cursiva naranja de 12 px se leía como pie de foto; este
              monitor se mira de pie y a un metro.
            */}
            {it.notasEspeciales && (
              <div className="mt-1 rounded-md border-l-4 border-orange-500 bg-orange-50 px-2 py-1 text-sm font-bold text-orange-900">
                📝 {it.notasEspeciales}
              </div>
            )}
          </div>
          );
        }) ?? <div className="text-xs text-slate-400">Cargando items...</div>}
      </div>

      <div className="border-t border-slate-200 pt-2 text-xs text-slate-500">
        Total: <span className="font-mono font-bold">
          {formatPrecio(pedido.total, monedaSimbolo, locale)}
        </span>
      </div>

      {estado === 'pendiente' && (
        <button
          onClick={() => onMarcarListo(pedido.id)}
          disabled={marcando}
          className="btn-primary mt-2 w-full"
        >
          <Check className="h-4 w-4" />
          {marcando ? 'Guardando...' : 'Marcar listo'}
        </button>
      )}
      {/*
        Sprint 66 — Paso 7: "ya está empaquetado".

        Sólo en domicilio y sólo cuando ya está listo: empaquetar es lo que pasa DESPUÉS de
        cocinar. Marcado, el botón se convierte en la hora, para que no se vuelva a apretar y
        para que la caja sepa desde cuándo está esperando el repartidor.
      */}
      {estado === 'listo' && esDomicilio && onMarcarEmpaquetado && (
        pedido.fechaEmpaquetado ? (
          <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-center text-sm font-medium text-emerald-800">
            <Package className="mr-1 inline h-4 w-4" />
            Empaquetado {new Date(pedido.fechaEmpaquetado).toLocaleTimeString('es-MX', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        ) : (
          <button
            onClick={() => onMarcarEmpaquetado(pedido.id)}
            disabled={marcandoEmpaquetado}
            className="btn-primary mt-2 w-full"
            title="Ya está en la bolsa, listo para el repartidor"
          >
            <Package className="h-4 w-4" />
            {marcandoEmpaquetado ? 'Guardando...' : 'Ya está empaquetado'}
          </button>
        )
      )}

      {/* Sprint 12.1d — Botón "Entregado en mesa" solo para comer_aqui en estado listo */}
      {estado === 'listo' && pedido.tipo === 'comer_aqui' && onMarcarEntregadoMesa && (
        <button
          onClick={() => onMarcarEntregadoMesa(pedido.id)}
          disabled={marcandoEntregado}
          className="btn-primary mt-2 w-full"
          title="El mesero ya llevó el plato a la mesa"
        >
          <Utensils className="h-4 w-4" />
          {marcandoEntregado ? 'Guardando...' : 'Entregado en mesa'}
        </button>
      )}
    </div>
  );
}

function PersonalizacionDetalle({ json }: { json: string }) {
  try {
    const personalizacion = JSON.parse(json);
    const incluidos = (personalizacion.componentes ?? [])
      .filter((c: { incluido: boolean }) => c.incluido)
      .map((c: { nombre: string }) => c.nombre);
    if (incluidos.length === 0) return null;
    return <div className="text-xs text-slate-500 pl-3">+ {incluidos.join(', ')}</div>;
  } catch {
    return null;
  }
}
